import React, { useContext, useMemo, useCallback } from 'react';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { getExpiresInfo } from 'lib/targetedOffers/Offers';
import { validateClosedBanners } from 'lib/targetedOffers/Banners/closedBanners';
import { getLastAppliedHours } from 'lib/targetedOffers/Banners/lastApplied';
import {
  BannerApplied,
  BannerExpires,
  BannerSoldOut,
  BannerDisabledOffers,
  PromotionsBanner,
} from '..';
import {
  BANNER_APPLIED,
  CLOSED_LYFE_CYCLE,
} from 'lib/targetedOffers/Banners/constants';

const PromoBanner = () => {
  const {
    targetedOfferApplied,
    targetedOffers,
    targetedOfferBannerSoldOut,
    targetedOffersDisabledMessage,
    promotions,
    promotionApplied,
  } = useContext(CruiseSearchContext);

  const hasPromotions = promotions?.valid && promotionApplied;
  const hasPromotionError = promotions?.error && !promotions?.valid;

  const listTargetedOffers = Array.isArray(targetedOffers)
    ? targetedOffers
    : [];

  const filteredOffers = listTargetedOffers?.map(val => {
    const { expiresSoon, expiresSameDay, expiresToday } = getExpiresInfo(
      new Date(),
      val.endDate,
    );
    if (expiresSameDay) return { ...val, expiresSameDay };
    if (expiresSoon || (!expiresSameDay && expiresToday))
      return { ...val, expiresSoon };
  });

  const offersExpires = filteredOffers.filter(val => val !== undefined);

  const hasApplied = targetedOfferApplied?.couponCode ? true : false;

  const isSoldOut = !hasApplied && targetedOfferBannerSoldOut;

  const hasExpires = Array.isArray(offersExpires)
    ? offersExpires.length > 0
    : false;

  const closedBanners = validateClosedBanners();

  const appliedWasClosed = closedBanners.some(
    closedBanner =>
      closedBanner.promoCode === targetedOfferApplied.code &&
      closedBanner.type === BANNER_APPLIED,
  );

  const { lastAppliedHours } = getLastAppliedHours();

  const showExpiresSoon =
    hasExpires &&
    !hasApplied &&
    (lastAppliedHours == undefined
      ? true
      : lastAppliedHours > CLOSED_LYFE_CYCLE);

  const getPriorityExpires = useMemo(() => {
    const expiresToday = offersExpires.filter(val =>
      Boolean(val.expiresSameDay),
    );
    const expiresSoon = offersExpires.filter(val => !val.expiresSameDay);
    if (expiresToday.length === 1) return [false, expiresToday[0]];
    if (expiresToday.length > 1) return [true, expiresToday[0]];
    if (expiresSoon.length == 1) return [false, expiresSoon[0]];
    if (expiresSoon.length > 1) return [true, expiresSoon[0]];
    return [];
  }, [offersExpires]);

  const getBanner = useCallback(() => {
    if (hasApplied && !appliedWasClosed){
      return <BannerApplied targetedOffer={targetedOfferApplied} />;
    }
    if (isSoldOut) return <BannerSoldOut />;
    if (showExpiresSoon) {
      const [isMultiple, offerExpires] = getPriorityExpires;
      if (!offerExpires) return null;
      return (
        <BannerExpires targetedOffer={offerExpires} multiple={isMultiple} />
      );
    }
    if (targetedOffersDisabledMessage?.title) {
      return <BannerDisabledOffers {...targetedOffersDisabledMessage} />;
    }
    if(hasPromotionError || hasPromotions){
      return <PromotionsBanner promotions={promotions} />
    }
  }, [
    hasApplied,
    appliedWasClosed,
    targetedOfferApplied,
    isSoldOut,
    showExpiresSoon,
    getPriorityExpires,
    hasPromotions,
    hasPromotionError,
  ]);

  return <>{getBanner()}</>;
};

export default PromoBanner;
